import React from 'react';
import { ArrowRight, Shield, Lock } from 'lucide-react';

interface HeroProps {
  onSecurityAssessmentClick: () => void;
  isDarkMode: boolean;
}

const Hero: React.FC<HeroProps> = ({ onSecurityAssessmentClick, isDarkMode }) => {
  return (
    <section className={`relative py-20 overflow-hidden ${
      isDarkMode 
        ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-slate-100' 
        : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 text-slate-900'
    }`}>
      {/* Enhanced Background Effects */}
      <div className="absolute inset-0">
        {/* Gradient Orbs */}
        <div className={`absolute top-10 left-10 w-32 h-32 rounded-full blur-3xl ${
          isDarkMode ? 'bg-blue-600/20' : 'bg-blue-500/30'
        }`}></div>
        <div className={`absolute top-20 right-20 w-24 h-24 rounded-full blur-2xl ${
          isDarkMode ? 'bg-purple-600/15' : 'bg-purple-500/25'
        }`}></div>
        <div className={`absolute bottom-20 left-20 w-28 h-28 rounded-full blur-3xl ${
          isDarkMode ? 'bg-indigo-600/20' : 'bg-indigo-500/30'
        }`}></div>
        <div className={`absolute bottom-10 right-10 w-20 h-20 rounded-full blur-2xl ${
          isDarkMode ? 'bg-cyan-600/15' : 'bg-cyan-500/25'
        }`}></div>
        
        {/* Mesh Gradient Overlay */}
        <div className={`absolute inset-0 ${
          isDarkMode 
            ? 'bg-gradient-to-r from-blue-900/10 via-transparent to-purple-900/10' 
            : 'bg-gradient-to-r from-blue-100/50 via-transparent to-purple-100/50'
        }`}></div>
        
        {/* SOC Environment Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div 
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat ${
              isDarkMode ? 'opacity-10' : 'opacity-15'
            }`}
            style={{
              backgroundImage: `url('https://res.cloudinary.com/durgvddoz/image/upload/v1755519336/ChatGPT_Image_Aug_18_2025_05_44_43_PM_fqpgx2.png')`
            }}
          />
          {/* Additional overlay for better text readability */}
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-b from-slate-950/50 via-transparent to-slate-950/50' 
              : 'bg-gradient-to-b from-blue-50/50 via-transparent to-purple-50/50'
          }`} />
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Enterprise <span className={isDarkMode ? "text-blue-400" : "text-blue-600"}>Cyber Security</span>
          </h1>
          <p className={`text-xl md:text-2xl mb-8 max-w-3xl mx-auto ${
            isDarkMode ? "text-slate-300" : "text-slate-600"
          }`}>
            Protect your business with advanced threat detection, 24/7 monitoring, 
            and expert security consulting tailored for modern enterprises.
          </p>
          <div className="flex justify-center">
            <button 
              onClick={onSecurityAssessmentClick}
              className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-lg ${
                isDarkMode 
                  ? "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-blue-500/25" 
                  : "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-blue-500/25"
              }`}
            >
              <Shield className="mr-2 h-5 w-5" />
              Get Security Assessment
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>
          
          {/* Security Animation */}
          <div className="flex items-center justify-center pointer-events-none mt-12">
            <div className="relative w-96 h-32">
              {/* First threat line */}
              <div className="absolute top-8 left-0 w-full h-0.5 overflow-hidden">
                <div className={`h-full w-16 animate-threat-move ${
                  isDarkMode ? 'bg-red-400' : 'bg-red-500'
                }`}></div>
              </div>
              
              {/* Second threat line */}
              <div className="absolute top-8 left-0 w-full h-0.5 overflow-hidden">
                <div className={`h-full w-12 animate-threat-move-delayed ${
                  isDarkMode ? 'bg-orange-400' : 'bg-orange-500'
                }`}></div>
              </div>
              
              {/* Third threat line */}
              <div className="absolute top-12 left-0 w-full h-0.5 overflow-hidden">
                <div className={`h-full w-14 animate-threat-move-slow ${
                  isDarkMode ? 'bg-yellow-400' : 'bg-yellow-500'
                }`}></div>
              </div>
              
              {/* Fourth threat line */}
              <div className="absolute top-16 left-0 w-full h-0.5 overflow-hidden">
                <div className={`h-full w-10 animate-threat-move-fast ${
                  isDarkMode ? 'bg-purple-400' : 'bg-purple-500'
                }`}></div>
              </div>
              
              {/* Fifth threat line */}
              <div className="absolute top-20 left-0 w-full h-0.5 overflow-hidden">
                <div className={`h-full w-18 animate-threat-move-extra ${
                  isDarkMode ? 'bg-pink-400' : 'bg-pink-500'
                }`}></div>
              </div>
              
              {/* Sixth threat line */}
              <div className="absolute top-24 left-0 w-full h-0.5 overflow-hidden">
                <div className={`h-full w-8 animate-threat-move-ultra ${
                  isDarkMode ? 'bg-cyan-400' : 'bg-cyan-500'
                }`}></div>
              </div>
              
              {/* Right to Left threat lines */}
              <div className="absolute top-6 left-0 w-full h-0.5 overflow-hidden">
                <div className={`h-full w-14 animate-threat-move-rtl ${
                  isDarkMode ? 'bg-emerald-400' : 'bg-emerald-500'
                }`}></div>
              </div>
              
              <div className="absolute top-10 left-0 w-full h-0.5 overflow-hidden">
                <div className={`h-full w-10 animate-threat-move-rtl-delayed ${
                  isDarkMode ? 'bg-teal-400' : 'bg-teal-500'
                }`}></div>
              </div>
              
              <div className="absolute top-14 left-0 w-full h-0.5 overflow-hidden">
                <div className={`h-full w-16 animate-threat-move-rtl-slow ${
                  isDarkMode ? 'bg-lime-400' : 'bg-lime-500'
                }`}></div>
              </div>
              
              <div className="absolute top-18 left-0 w-full h-0.5 overflow-hidden">
                <div className={`h-full w-12 animate-threat-move-rtl-fast ${
                  isDarkMode ? 'bg-amber-400' : 'bg-amber-500'
                }`}></div>
              </div>
              
              <div className="absolute top-22 left-0 w-full h-0.5 overflow-hidden">
                <div className={`h-full w-18 animate-threat-move-rtl-extra ${
                  isDarkMode ? 'bg-rose-400' : 'bg-rose-500'
                }`}></div>
              </div>
              
              {/* Shield Protection */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <Shield className={`h-16 w-16 animate-pulse ${
                  isDarkMode ? 'text-blue-400' : 'text-blue-600'
                }`} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;